import React from 'react';
import type { Item, RichTextDescription } from '../../types/game';
import { renderRichText, plainTextToRichText, isRichTextDescription } from '../../utils/richTextRenderer';

export interface InventoryItem {
  id: string;
  name: string;
  description?: string | RichTextDescription | undefined;
  quantity?: number;
  type?: 'item' | 'tool' | 'consumable' | 'key';
  fullItem?: Item; // Full Item object for rich text rendering
}

export interface ItemCardProps {
  item: InventoryItem;
  onItemClick?: ((item: InventoryItem) => void) | undefined;
  onItemUse?: ((item: InventoryItem) => void) | undefined;
  className?: string;
  style?: React.CSSProperties;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  onItemClick,
  onItemUse,
  className = '',
  style = {}
}) => {
  const getItemTypeColor = (type?: string) => {
    switch (type) {
      case 'tool':
        return 'bg-orange-100 text-orange-800';
      case 'consumable':
        return 'bg-green-100 text-green-800';
      case 'key':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getItemIcon = (type?: string) => {
    switch (type) {
      case 'tool':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
        );
      case 'consumable':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'key':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.75rem',
        padding: '1rem',
        transition: 'all 0.3s ease-in-out',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        cursor: onItemClick ? 'pointer' : 'default',
        ...style
      }}
      className={`hover:shadow-lg hover:border-white/30 hover:bg-black/40 hover:-translate-y-1 ${className}`}
      onClick={() => onItemClick?.(item)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <div className={`p-2 rounded-lg ${getItemTypeColor(item.type)}`}>
            {getItemIcon(item.type)}
          </div>
          <div className="flex-1">
            <h4 
              className="font-medium text-base"
              style={{ color: '#ffffff' }}
            >
              {item.name}
            </h4>
            {item.description && (
              <div 
                className="text-sm rich-text mt-1"
                style={{ color: '#e5e7eb' }}
                dangerouslySetInnerHTML={{
                  __html: item.fullItem && item.fullItem.description
                    ? (isRichTextDescription(item.fullItem.description)
                        ? renderRichText(item.fullItem.description)
                        : renderRichText(plainTextToRichText(item.fullItem.description)))
                    : (item.description 
                        ? (typeof item.description === 'string' 
                            ? renderRichText(plainTextToRichText(item.description))
                            : renderRichText(item.description))
                        : renderRichText(plainTextToRichText('')))
                }}
              />
            )}
            {item.quantity && item.quantity > 1 && (
              <span 
                className="text-xs mt-1 inline-block"
                style={{ color: '#9ca3af' }}
              >
                x{item.quantity}
              </span>
            )}
          </div>
        </div>
        
        {onItemUse && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onItemUse(item);
            }}
            className="px-3 py-1 text-sm rounded-md transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              color: '#93c5fd',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            }}
          >
            Use
          </button>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
